import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { TableSort } from "@src/shared/constants/sort-order.constants";
import {
  userGuideCategoryService,
  type UserGuideStatusType,
  type UserGuideCategoryListItem,
  type UserGuideCategoryOption,
  type GetUserGuideCategoriesParams,
  type CreateUserGuideCategoryPayload,
  type UpdateUserGuideCategoryPayload,
} from "@src/@api-services/userGuide";

interface UserGuideCategoryState {
  data: UserGuideCategoryListItem[];
  allCategories: UserGuideCategoryOption[];
  total: number;
  params: GetUserGuideCategoriesParams;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getUserGuideCategories = createAsyncThunk(
  "userGuideCategories/getUserGuideCategories",
  async (params: GetUserGuideCategoriesParams, { rejectWithValue }) => {
    try {
      const response = await userGuideCategoryService.getCategories(params);
      const rawData = response.data?.data?.data ?? [];
      const totalRecord = response.data?.data?.totalRecord ?? 0;
      return {
        params,
        data: rawData as UserGuideCategoryListItem[],
        total: Number(totalRecord),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch user guide categories"
      );
    }
  }
);

export const getAllUserGuideCategories = createAsyncThunk(
  "userGuideCategories/getAllUserGuideCategories",
  async (status: UserGuideStatusType, { rejectWithValue }) => {
    try {
      const response = await userGuideCategoryService.getAllCategories(status);
      return (response.data?.data ?? []) as UserGuideCategoryOption[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch category options"
      );
    }
  }
);

export const createUserGuideCategory = createAsyncThunk(
  "userGuideCategories/createUserGuideCategory",
  async (
    payload: CreateUserGuideCategoryPayload,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await userGuideCategoryService.createCategory(payload);
      const state = getState() as any;
      if (state.userGuideCategories?.params) {
        await dispatch(getUserGuideCategories(state.userGuideCategories.params));
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to create category"
      );
    }
  }
);

export const updateUserGuideCategory = createAsyncThunk(
  "userGuideCategories/updateUserGuideCategory",
  async (
    {
      id,
      categoryData,
    }: {
      id: string | number;
      categoryData: UpdateUserGuideCategoryPayload;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await userGuideCategoryService.updateCategory(
        id,
        categoryData
      );
      const state = getState() as any;
      if (state.userGuideCategories?.params) {
        await dispatch(getUserGuideCategories(state.userGuideCategories.params));
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update category"
      );
    }
  }
);

export const deleteUserGuideCategory = createAsyncThunk(
  "userGuideCategories/deleteUserGuideCategory",
  async (id: string | number, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await userGuideCategoryService.deleteCategory(id);
      const state = getState() as any;
      if (state.userGuideCategories?.params) {
        await dispatch(getUserGuideCategories(state.userGuideCategories.params));
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete category"
      );
    }
  }
);

const initialState: UserGuideCategoryState = {
  data: [],
  allCategories: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  loading: false,
  actionLoading: false,
  error: null,
};

export const userGuideCategorySlice = createSlice({
  name: "userGuideCategories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // List
      .addCase(getUserGuideCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserGuideCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getUserGuideCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load categories";
      })
      // All
      .addCase(getAllUserGuideCategories.fulfilled, (state, action) => {
        state.allCategories = action.payload;
      })
      // Mutate loading
      .addCase(createUserGuideCategory.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createUserGuideCategory.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(createUserGuideCategory.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateUserGuideCategory.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateUserGuideCategory.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateUserGuideCategory.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteUserGuideCategory.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteUserGuideCategory.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteUserGuideCategory.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export default userGuideCategorySlice.reducer;
